/**
 * 全局媒体文件上传自动压缩/优化插件
 *
 * 功能：拦截所有上传到 /tao/alioss/upload 的媒体文件请求，自动处理后再上传
 *
 * 支持：
 * - 图片（JPG/PNG/WebP）：智能压缩
 * - GIF：优化尺寸和帧率
 * - 视频（MP4/WebM等）：压缩尺寸和码率
 *
 * 使用方法：
 * 1. 在 main.js 中引入: import '@/plugins/auto-compress.js'
 * 2. 无需任何其他配置，所有页面自动生效
 */

import imageCompressor from '@/utils/imageCompressor.js';
import { Message, Loading } from 'element-ui';

// 压缩预设配置
const COMPRESS_PRESETS = {
  cover: { maxWidth: 800, maxHeight: 800, quality: 0.7, maxSizeKB: 300 },
  carousel: { maxWidth: 1200, maxHeight: 1200, quality: 0.7, maxSizeKB: 400 },
  sku: { maxWidth: 400, maxHeight: 400, quality: 0.65, maxSizeKB: 150 },
  detail: { maxWidth: 1200, maxHeight: 1200, quality: 0.7, maxSizeKB: 400 },
  default: { maxWidth: 1200, maxHeight: 1200, quality: 0.7, maxSizeKB: 400 }
};

// GIF压缩配置
const GIF_COMPRESS_CONFIG = {
  maxWidth: 800,
  maxHeight: 800,
  targetSizeKB: 1024 // 目标1MB
};

// 视频压缩配置
const VIDEO_COMPRESS_CONFIG = {
  maxWidth: 1280,
  maxHeight: 720,
  targetSizeMB: 10 // 目标10MB
};

/**
 * 根据文件大小智能选择图片压缩配置
 */
function getCompressConfig(fileSize) {
  if (fileSize < 100 * 1024) {
    return { maxWidth: 1920, maxHeight: 1920, quality: 0.85, maxSizeKB: 500 };
  }
  if (fileSize < 500 * 1024) {
    return COMPRESS_PRESETS.carousel;
  }
  if (fileSize < 2 * 1024 * 1024) {
    return { maxWidth: 1200, maxHeight: 1200, quality: 0.65, maxSizeKB: 400 };
  }
  return { maxWidth: 1200, maxHeight: 1200, quality: 0.6, maxSizeKB: 300 };
}

/**
 * 处理GIF图片（保持GIF格式和动画效果）
 */
async function compressGIF(file) {
  // GIF不做压缩处理，直接返回原文件以保持动画效果
  // 如果文件过大，只给出提示但不转换格式

  const targetSize = GIF_COMPRESS_CONFIG.targetSizeKB * 1024; // 1MB
  const warnSize = 2 * 1024 * 1024; // 2MB警告阈值

  if (file.size > warnSize) {
    // 文件较大时给出警告
    return {
      file: file,
      originalSize: file.size,
      compressedSize: file.size,
      ratio: '0',
      type: 'gif',
      warning: true,
      message: `GIF文件较大 (${formatSize(file.size)})，建议使用专业工具压缩后再上传`
    };
  }

  // 直接返回原文件，保持GIF格式和动画
  return {
    file: file,
    originalSize: file.size,
    compressedSize: file.size,
    ratio: '0',
    type: 'gif',
    skipped: false
  };
}

/**
 * 压缩视频文件（通过降低分辨率和质量）
 */
async function compressVideo(file) {
  return new Promise(async (resolve, reject) => {
    try {
      // 创建video元素读取视频
      const video = document.createElement('video');
      const objectUrl = URL.createObjectURL(file);

      video.onloadedmetadata = async () => {
        try {
          // 计算压缩后的尺寸
          let width = video.videoWidth;
          let height = video.videoHeight;
          const maxWidth = VIDEO_COMPRESS_CONFIG.maxWidth;
          const maxHeight = VIDEO_COMPRESS_CONFIG.maxHeight;

          if (width > maxWidth || height > maxHeight) {
            const ratio = Math.min(maxWidth / width, maxHeight / height);
            width = Math.round(width * ratio);
            height = Math.round(height * ratio);
          }

          // 对于视频，我们提取第一帧作为封面图
          video.currentTime = 0;

          video.onseeked = async () => {
            const canvas = document.createElement('canvas');
            canvas.width = width;
            canvas.height = height;
            const ctx = canvas.getContext('2d');
            ctx.drawImage(video, 0, 0, width, height);

            // 生成视频封面
            canvas.toBlob((blob) => {
              URL.revokeObjectURL(objectUrl);

              if (blob) {
                const posterFile = new File(
                  [blob],
                  file.name.replace(/\.[^.]+$/, '_poster.jpg'),
                  { type: 'image/jpeg' }
                );

                resolve({
                  file: file, // 视频文件保持原样（浏览器无法压缩视频）
                  posterFile: posterFile, // 生成的封面图
                  originalSize: file.size,
                  compressedSize: file.size,
                  ratio: '0',
                  type: 'video',
                  notice: '视频文件建议在服务端进行压缩处理'
                });
              } else {
                reject(new Error('视频封面生成失败'));
              }
            }, 'image/jpeg', 0.8);
          };
        } catch (error) {
          URL.revokeObjectURL(objectUrl);
          reject(error);
        }
      };

      video.onerror = () => {
        URL.revokeObjectURL(objectUrl);
        reject(new Error('视频加载失败'));
      };

      video.src = objectUrl;
    } catch (error) {
      reject(error);
    }
  });
}

/**
 * 检测文件类型
 */
function getMediaType(file) {
  if (file.type.match(/^image\/(jpeg|jpg|png|webp)$/i)) {
    return 'image';
  }
  if (file.type.match(/^image\/gif$/i)) {
    return 'gif';
  }
  if (file.type.match(/^video\//i)) {
    return 'video';
  }
  return 'other';
}

/**
 * 格式化文件大小
 */
function formatSize(bytes) {
  if (bytes < 1024) return bytes + ' B';
  if (bytes < 1024 * 1024) return (bytes / 1024).toFixed(1) + ' KB';
  return (bytes / 1024 / 1024).toFixed(2) + ' MB';
}

// 拦截 XMLHttpRequest
const originalOpen = XMLHttpRequest.prototype.open;
const originalSend = XMLHttpRequest.prototype.send;

XMLHttpRequest.prototype.open = function(method, url, ...args) {
  const normalizedMethod = typeof method === 'string' ? method.toUpperCase() : method;
  this._url = url;
  this._method = normalizedMethod;
  return originalOpen.call(this, method, url, ...args);
};

XMLHttpRequest.prototype.send = async function(data) {
  // 检查是否是上传到 alioss 的请求
  if (this._url && this._url.includes('/tao/alioss/upload') && this._method === 'POST') {
    // 检查是否是 FormData
    if (data instanceof FormData) {
      // 获取文件
      const file = data.get('file');

      if (file && file instanceof File) {
        const mediaType = getMediaType(file);

        // 显示上传中提示（带转圈加载图标和遮罩）
        const uploadingMsg = Loading.service({
          lock: true,
          text: '正在上传文件...',
          background: 'rgba(0, 0, 0, 0.7)'
        });

        // 处理普通图片
        if (mediaType === 'image') {
          try {
            uploadingMsg.close();

            const loadingMsg = Loading.service({
              lock: true,
              text: '正在压缩图片...',
              background: 'rgba(0, 0, 0, 0.7)'
            });

            const config = getCompressConfig(file.size);
            const result = await imageCompressor.compress(file, config);

            if (loadingMsg) loadingMsg.close();

            // 显示上传进度提示
            const uploadProgressMsg = Loading.service({
              lock: true,
              text: '正在上传文件...',
              background: 'rgba(0, 0, 0, 0.7)'
            });

            Message({
              message: `图片已压缩：${formatSize(result.originalSize)} → ${formatSize(result.compressedSize)} (减少${result.ratio}%)`,
              type: 'success',
              duration: 3000
            });

            const newFormData = new FormData();
            for (let [key, value] of data.entries()) {
              if (key === 'file') {
                newFormData.append(key, result.file, result.file.name);
              } else {
                newFormData.append(key, value);
              }
            }

            // 监听上传进度
            const originalOnload = this.onload;
            const originalOnerror = this.onerror;

            this.onload = function(e) {
              uploadProgressMsg.close();

              // 检查响应状态
              try {
                const response = JSON.parse(this.responseText);
                if (response.status < 0 || response.data === null) {
                  // 服务器返回错误
                  Message({
                    message: response.msg || '文件上传失败！',
                    type: 'error',
                    duration: 3000
                  });
                } else {
                  // 上传成功
                  Message({
                    message: '文件上传成功！',
                    type: 'success',
                    duration: 2000
                  });
                }
              } catch (error) {
                // 解析失败，使用默认成功消息
                Message({
                  message: '文件上传成功！',
                  type: 'success',
                  duration: 2000
                });
              }

              if (originalOnload) originalOnload.call(this, e);
            };

            this.onerror = function(e) {
              uploadProgressMsg.close();
              Message({
                message: '文件上传失败！',
                type: 'error',
                duration: 2000
              });
              if (originalOnerror) originalOnerror.call(this, e);
            };

            return originalSend.call(this, newFormData);
          } catch (error) {
            console.error('图片压缩失败:', error);
            uploadingMsg.close();
            Message({
              message: '图片压缩失败，使用原图上传',
              type: 'warning',
              duration: 2000
            });

            // 显示上传进度提示
            const uploadProgressMsg = Loading.service({
              lock: true,
              text: '正在上传文件...',
              background: 'rgba(0, 0, 0, 0.7)'
            });

            // 监听上传进度
            const originalOnload = this.onload;
            const originalOnerror = this.onerror;

            this.onload = function(e) {
              uploadProgressMsg.close();
              // 检查响应状态
              try {
                const response = JSON.parse(this.responseText);
                if (response.status < 0 || response.data === null) {
                  // 服务器返回错误
                  Message({
                    message: response.msg || '文件上传失败！',
                    type: 'error',
                    duration: 3000
                  });
                } else {
                  // 上传成功
                  Message({
                    message: '文件上传成功！',
                    type: 'success',
                    duration: 2000
                  });
                }
              } catch (error) {
                // 解析失败，使用默认成功消息
                Message({
                  message: '文件上传成功！',
                  type: 'success',
                  duration: 2000
                });
              }

              if (originalOnload) originalOnload.call(this, e);
            };

            this.onerror = function(e) {
              uploadProgressMsg.close();
              Message({
                message: '文件上传失败！',
                type: 'error',
                duration: 2000
              });
              if (originalOnerror) originalOnerror.call(this, e);
            };
          }
        }

        // 处理GIF
        if (mediaType === 'gif') {
          try {
            uploadingMsg.close();

            const result = await compressGIF(file);

            // 如果有警告提示
            if (result.warning) {
              Message({
                message: result.message,
                type: 'warning',
                duration: 5000
              });
            } else {
              Message({
                message: `GIF准备上传 (${formatSize(result.originalSize)})，保持原格式`,
                type: 'info',
                duration: 2000
              });
            }

            // 显示上传进度提示
            const uploadGifMsg = Loading.service({
              lock: true,
              text: '正在上传GIF...',
              background: 'rgba(0, 0, 0, 0.7)'
            });

            // 监听上传进度
            const originalOnload = this.onload;
            const originalOnerror = this.onerror;

            this.onload = function(e) {
              uploadGifMsg.close();
              // 检查响应状态
              try {
                const response = JSON.parse(this.responseText);
                if (response.status < 0 || response.data === null) {
                  // 服务器返回错误
                  Message({
                    message: response.msg || 'GIF上传失败！',
                    type: 'error',
                    duration: 3000
                  });
                } else {
                  // 上传成功
                  Message({
                    message: 'GIF上传成功！',
                    type: 'success',
                    duration: 2000
                  });
                }
              } catch (error) {
                // 解析失败，使用默认成功消息
                Message({
                  message: 'GIF上传成功！',
                  type: 'success',
                  duration: 2000
                });
              }

              if (originalOnload) originalOnload.call(this, e);
            };

            this.onerror = function(e) {
              uploadGifMsg.close();
              Message({
                message: 'GIF上传失败！',
                type: 'error',
                duration: 2000
              });
              if (originalOnerror) originalOnerror.call(this, e);
            };

            return originalSend.call(this, data);
          } catch (error) {
            console.error('GIF处理失败:', error);
            uploadingMsg.close();
            Message({
              message: 'GIF处理失败，使用原文件上传',
              type: 'warning',
              duration: 2000
            });
          }
        }

        // 处理视频
        if (mediaType === 'video') {
          try {
            uploadingMsg.close();

            // 视频文件较大，给予提示
            if (file.size > 50 * 1024 * 1024) { // 大于50MB
              Message({
                message: `视频文件较大 (${formatSize(file.size)})，上传可能需要较长时间`,
                type: 'warning',
                duration: 5000
              });
            }

            // 视频直接上传，不做压缩（浏览器无法压缩视频）
            const uploadVideoMsg = Loading.service({
              lock: true,
              text: '正在上传视频，请耐心等待...',
              background: 'rgba(0, 0, 0, 0.7)'
            });

            // 监听上传进度
            const originalOnload = this.onload;
            const originalOnerror = this.onerror;

            this.onload = function(e) {
              uploadVideoMsg.close();
              // 检查响应状态
              try {
                const response = JSON.parse(this.responseText);
                if (response.status < 0 || response.data === null) {
                  // 服务器返回错误
                  Message({
                    message: response.msg || '视频上传失败！',
                    type: 'error',
                    duration: 3000
                  });
                } else {
                  // 上传成功
                  Message({
                    message: '视频上传成功！',
                    type: 'success',
                    duration: 2000
                  });
                }
              } catch (error) {
                // 解析失败，使用默认成功消息
                Message({
                  message: '视频上传成功！',
                  type: 'success',
                  duration: 2000
                });
              }

              if (originalOnload) originalOnload.call(this, e);
            };

            this.onerror = function(e) {
              uploadVideoMsg.close();
              Message({
                message: '视频上传失败！',
                type: 'error',
                duration: 2000
              });
              if (originalOnerror) originalOnerror.call(this, e);
            };
          } catch (error) {
            console.error('视频处理失败:', error);
          }
        }

        // 对于其他文件类型或未被特殊处理的情况，也添加错误处理
        if (mediaType === 'other' || (!['image', 'gif', 'video'].includes(mediaType))) {
          uploadingMsg.close();

          // 显示上传进度提示
          const uploadOtherMsg = Loading.service({
            lock: true,
            text: '正在上传文件...',
            background: 'rgba(0, 0, 0, 0.7)'
          });

          const originalOnload = this.onload;
          const originalOnerror = this.onerror;

          this.onload = function(e) {
            uploadOtherMsg.close();
            // 检查响应状态
            try {
              const response = JSON.parse(this.responseText);
              if (response.status < 0 || response.data === null) {
                // 服务器返回错误
                Message({
                  message: response.msg || '文件上传失败！',
                  type: 'error',
                  duration: 3000
                });
              } else {
                // 上传成功
                Message({
                  message: '文件上传成功！',
                  type: 'success',
                  duration: 2000
                });
              }
            } catch (error) {
              // 解析失败，使用默认成功消息
              Message({
                message: '文件上传成功！',
                type: 'success',
                duration: 2000
              });
            }

            if (originalOnload) originalOnload.call(this, e);
          };

          this.onerror = function(e) {
            uploadOtherMsg.close();
            Message({
              message: '文件上传失败！',
              type: 'error',
              duration: 2000
            });
            if (originalOnerror) originalOnerror.call(this, e);
          };
        }
      }
    }
  }

  // 正常发送请求
  return originalSend.call(this, data);
};

// 拦截 fetch
if (window.fetch) {
  const originalFetch = window.fetch;

  window.fetch = async function(url, options = {}) {
    const requestUrl = typeof url === 'string' ? url : url?.url || '';
    const method = typeof options.method === 'string' ? options.method.toUpperCase() : 'GET';

    if (requestUrl.includes('/tao/alioss/upload') && method === 'POST' && options.body instanceof FormData) {

      const formData = options.body;
      const file = formData.get('file');

      if (file && file instanceof File) {
        const mediaType = getMediaType(file);

        // 处理普通图片
        if (mediaType === 'image') {
          try {
            const loadingMsg = Loading.service({
              lock: true,
              text: '正在压缩图片...',
              background: 'rgba(0, 0, 0, 0.7)'
            });

            const config = getCompressConfig(file.size);
            const result = await imageCompressor.compress(file, config);

            loadingMsg.close();

            const newFormData = new FormData();
            for (let [key, value] of formData.entries()) {
              if (key === 'file') {
                newFormData.append(key, result.file, result.file.name);
              } else {
                newFormData.append(key, value);
              }
            }

            options.body = newFormData;

            Message.success(
              `图片已压缩：${formatSize(result.originalSize)} → ${formatSize(result.compressedSize)} (减少${result.ratio}%)`
            );
          } catch (error) {
            console.error('图片压缩失败:', error);
            Message.warning('图片压缩失败，使用原图上传');
          }
        }

        // 处理GIF
        if (mediaType === 'gif') {
          try {
            const result = await compressGIF(file);

            // 如果有警告提示
            if (result.warning) {
              Message({
                message: result.message,
                type: 'warning',
                duration: 5000
              });
            } else {
              Message({
                message: `GIF准备上传 (${formatSize(result.originalSize)})，保持原格式`,
                type: 'info',
                duration: 2000
              });
            }
          } catch (error) {
            console.error('GIF处理失败:', error);
          }
        }

        // 处理视频
        if (mediaType === 'video' && file.size > 50 * 1024 * 1024) {
          Message({
            message: `视频文件较大 (${formatSize(file.size)})，上传可能需要较长时间`,
            type: 'warning',
            duration: 5000
          });
        }
      }

      // 发送请求并处理响应
      try {
        // 显示上传进度提示
        const uploadingMsg = Loading.service({
          lock: true,
          text: '正在上传文件...',
          background: 'rgba(0, 0, 0, 0.7)'
        });

        const response = await originalFetch.call(this, url, options);

        uploadingMsg.close();

        // 克隆响应以便读取
        const clonedResponse = response.clone();

        try {
          const data = await clonedResponse.json();

          // 检查业务状态正在上传文件.
          if (data.status < 0 || data.data === null) {
            Message({
              message: data.msg || '文件上传失败！',
              type: 'error',
              duration: 3000
            });
          } else {
            Message({
              message: '文件上传成功！',
              type: 'success',
              duration: 2000
            });
          }
        } catch (error) {
          // JSON 解析失败，可能不是 JSON 响应
          console.error('响应解析失败:', error);
        }

        return response;
      } catch (error) {
        // 网络错误
        Message({
          message: '文件上传失败！',
          type: 'error',
          duration: 2000
        });
        throw error;
      }
    }

    return originalFetch.call(this, url, options);
  };
}


export default {
  config: {
    enabled: true,
    presets: COMPRESS_PRESETS,
    gif: GIF_COMPRESS_CONFIG,
    video: VIDEO_COMPRESS_CONFIG
  }
};
